import { formatNumber } from '@angular/common';
import {
  FormGroup,
  FormControl,
  Validators,
  FormArray,
  AbstractControl
} from '@angular/forms';
import {
  Component,
  OnInit,
  Renderer2,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { forkJoin } from 'rxjs';
import { TradesCaptureService, LegService } from '@approot/services';
import { KeyValue, RequestStatus, Entity, Csa } from '@approot/models';
import { Utilities, isTrueLike } from '@approot/utilities';
import csaCurrenciesJson from './csa-currencies.json';
@Component({
  selector: 'app-csa',
  templateUrl: './csa.component.html',
  styleUrls: ['./csa.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsaComponent implements OnInit {
  public selectedCsaIndex: number = null;
  public csaForm: FormGroup;
  public identifiers = [];
  public sameGroupIdentifiers = [];
  public loadingData = true;
  public savingCsaStatus: RequestStatus = '';
  public csasToDelete = [];
  public csaCurrencies = csaCurrenciesJson;
  @ViewChild('divToScroll', { static: false }) public divToScroll: ElementRef;
  public reasonCantSave:
    | 'MISSING_INTERNAL_ENTITY'
    | 'SAME_COUNTERPARTIES'
    | null = null;

  constructor(
    private renderer: Renderer2,
    private tradesCaptureService: TradesCaptureService,
    private legService: LegService,
    private changeRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.csaForm = new FormGroup({
      csaArray: new FormArray([])
    });

    let requests = {};

    requests['entities'] = this.tradesCaptureService.getEntities();

    requests['csa'] = this.tradesCaptureService.getUserInfoRequest({
      task: 'getAll',
      table: 'csa'
    });

    forkJoin(requests).subscribe({
      next: (response: { entities: Entity[]; csa: Csa[] }) => {
        const entities = response.entities;
        const csa = response.csa;
        this.updateLocalEntities(entities);

        this.sameGroupIdentifiers = entities
          .filter(entity => isTrueLike(entity.isMemberOfParentGroup))
          .map(entity => entity.identifier);

        csa.forEach(curveItem => {
          if (
            !this.identifiers.includes(curveItem['counterPartyA']) ||
            !this.identifiers.includes(curveItem['counterPartyB'])
          ) {
            return; // hide rows where a counterParty doesn't exist
          }

          const curveFormGroup = this.createCreditCurveForm(curveItem);

          (this.csaForm.get('csaArray') as FormArray).push(curveFormGroup);
        });
        this.loadingData = false;
        this.changeRef.markForCheck();
      }
    });
  }

  createCreditCurveForm(curveItem: Csa) {
    const curveFormGroup = new FormGroup({
      counterPartyA: new FormControl(
        curveItem && curveItem['counterPartyA']
          ? curveItem['counterPartyA']
          : ''
      ),
      counterPartyB: new FormControl(
        curveItem && curveItem['counterPartyB']
          ? curveItem['counterPartyB']
          : ''
      ),
      'counterPartyA:posts': new FormControl(
        curveItem && curveItem['counterPartyA:posts']
          ? curveItem['counterPartyA:posts']
          : '',
        Validators.required
      ),
      'counterPartyB:posts': new FormControl(
        curveItem && curveItem['counterPartyB:posts']
          ? curveItem['counterPartyB:posts']
          : '',
        Validators.required
      ),
      currency: new FormControl(
        curveItem && curveItem.currency ? curveItem.currency : '',
        Validators.required
      ),
      'counterPartyA:threshold': this.formControlNumberOnly(
        curveItem,
        'counterPartyA:threshold'
      ),
      'counterPartyB:threshold': this.formControlNumberOnly(
        curveItem,
        'counterPartyB:threshold'
      ),
      'counterPartyA:independentAmount': this.formControlNumberOnly(
        curveItem,
        'counterPartyA:independentAmount'
      ),
      'counterPartyB:independentAmount': this.formControlNumberOnly(
        curveItem,
        'counterPartyB:independentAmount'
      ),
      'counterPartyA:postedVariationMargin': this.formControlNumberOnly(
        curveItem,
        'counterPartyA:postedVariationMargin'
      ),
      'counterPartyB:postedVariationMargin': this.formControlNumberOnly(
        curveItem,
        'counterPartyB:postedVariationMargin'
      ),
      mta: this.formControlNumberOnly(curveItem, 'mta'),
      saved: new FormControl({
        value: curveItem ? true : false,
        disabled: true
      })
    });
    curveFormGroup
      .get('counterPartyA')
      .setValidators([
        Validators.required,
        this.counterPartyValidator.bind(this, 'counterPartyB')
      ]);

    curveFormGroup
      .get('counterPartyB')
      .setValidators([
        Validators.required,
        this.counterPartyValidator.bind(this, 'counterPartyA')
      ]);

    curveFormGroup.get('currency').valueChanges.subscribe(val => {
      this.textBoxAdderPrep(
        curveFormGroup.get('counterPartyA:threshold'),
        val,
        'currency'
      );

      this.textBoxAdderPrep(
        curveFormGroup.get('counterPartyB:threshold'),
        val,
        'currency'
      );

      this.textBoxAdderPrep(
        curveFormGroup.get('counterPartyA:independentAmount'),
        val,
        'currency'
      );

      this.textBoxAdderPrep(
        curveFormGroup.get('counterPartyB:independentAmount'),
        val,
        'currency'
      );

      this.textBoxAdderPrep(
        curveFormGroup.get('counterPartyA:postedVariationMargin'),
        val,
        'currency'
      );

      this.textBoxAdderPrep(
        curveFormGroup.get('counterPartyB:postedVariationMargin'),
        val,
        'currency'
      );

      this.textBoxAdderPrep(curveFormGroup.get('mta'), val, 'currency');
    });

    return curveFormGroup;
  }

  textBoxAdderPrep(
    formControl: AbstractControl,
    currencyValue: string,
    typeToAdd: 'currency',
    event?: InputEvent
  ) {
    if (currencyValue) {
      Utilities.endOfTextBoxAdder(
        event,
        formControl,
        ' ' + currencyValue,
        !!formControl.value,
        typeToAdd,
        this.renderer
      );
    }
  }

  amountTextBoxRestrictionHandler(event, action) {
    // used to get the index of the item separating editable parts of text and non editable parts
    const indexLocator = Utilities.getIndexOfSeparator(event.target.value, ' ');
    if (action === 'keydown') {
      Utilities.textBoxKeydownRestrictor(event, indexLocator, this.renderer);
    } else {
      Utilities.textBoxClickRestrictor(event, indexLocator, this.renderer);
    }
  }

  formControlNumberOnly(curveItem: Csa, itemName: string) {
    return new FormControl(
      curveItem &&
      !isNaN(curveItem[itemName]) &&
      curveItem[itemName] !== undefined
        ? formatNumber(curveItem[itemName], 'en-GB', '1.0-0') +
          ' ' +
          curveItem.currency
        : '',
      Validators.required
    );
  }

  get showcsa(): boolean {
    return this.tradesCaptureService.showCsa;
  }

  addNewCsa() {
    const curveFormGroup = this.createCreditCurveForm(null);
    (this.csaForm.get('csaArray') as FormArray).push(curveFormGroup);

    setTimeout(() => {
      this.updateScroll();
    }, 0);
  }

  get csaArray(): FormArray {
    return this.csaForm.get('csaArray') as FormArray;
  }

  saveChanges() {
    const changedRows = (
      this.csaForm.get('csaArray') as FormArray
    ).controls.filter(entity => entity.dirty === true); // get all the rows that have been altered

    let changes = {};

    changedRows.forEach((row: FormGroup, index: number) => {
      const rowValue = row.value;
      rowValue.identifier =
        rowValue.counterPartyA < rowValue.counterPartyB
          ? rowValue.counterPartyA + ' | ' + rowValue.counterPartyB
          : rowValue.counterPartyB + ' | ' + rowValue.counterPartyA;

      rowValue['counterPartyA:threshold'] = Utilities.formattedNumberToNumber(
        rowValue['counterPartyA:threshold']
      );

      rowValue['counterPartyB:threshold'] = Utilities.formattedNumberToNumber(
        rowValue['counterPartyB:threshold']
      );

      rowValue['counterPartyA:independentAmount'] =
        Utilities.formattedNumberToNumber(
          rowValue['counterPartyA:independentAmount']
        );

      rowValue['counterPartyB:independentAmount'] =
        Utilities.formattedNumberToNumber(
          rowValue['counterPartyB:independentAmount']
        );

      rowValue['counterPartyA:postedVariationMargin'] =
        Utilities.formattedNumberToNumber(
          rowValue['counterPartyA:postedVariationMargin']
        );

      rowValue['counterPartyB:postedVariationMargin'] =
        Utilities.formattedNumberToNumber(
          rowValue['counterPartyB:postedVariationMargin']
        );

      rowValue['mta'] = Utilities.formattedNumberToNumber(rowValue['mta']);

      changes['save' + index] = this.tradesCaptureService.getUserInfoRequest({
        task: 'putItem',
        table: 'csa',
        item: rowValue
      });
    });

    this.csasToDelete.forEach((row: any, index: number) => {
      changes['delete' + index] = this.tradesCaptureService.getUserInfoRequest({
        task: 'deleteItem',
        table: 'csa',
        key: 'identifier',
        identifier:
          row.counterPartyA < row.counterPartyB
            ? row.counterPartyA + ' | ' + row.counterPartyB
            : row.counterPartyB + ' | ' + row.counterPartyA
      });
    });
    this.savingCsaStatus = 'LOADING';
    forkJoin(changes).subscribe({
      next: () => {
        changedRows.forEach((row: FormControl) => {
          row.get('saved').setValue(true);
        });
        this.csaForm.markAsPristine();
        this.csasToDelete = [];
        this.savingCsaStatus = 'DONE';
        this.changeRef.markForCheck();
        this.resetSavingStatus();
      },
      error: () => {
        this.savingCsaStatus = 'ERROR';
        this.changeRef.markForCheck();
        this.resetSavingStatus();
      }
    });
  }

  resetSavingStatus() {
    setTimeout(() => {
      this.savingCsaStatus = '';
      this.changeRef.markForCheck();
    }, 1500);
  }

  deleteCsa() {
    const csaToDelete = this.csaArray.controls[
      this.selectedCsaIndex
    ] as FormGroup;

    if (csaToDelete.get('saved').value) {
      this.csasToDelete.push(csaToDelete.value);
    }

    this.csaArray.removeAt(this.selectedCsaIndex);
    this.selectedCsaIndex = null;
  }

  updateScroll() {
    let e = this.divToScroll.nativeElement;
    e.scrollTop = e.scrollHeight;
  }

  get allowedToSave(): boolean {
    return (
      this.savingCsaStatus !== 'LOADING' &&
      this.csaForm.valid &&
      ((!this.csaForm.pristine && this.csaArray.controls.length > 0) ||
        this.csasToDelete.length > 0)
    );
  }

  counterPartyValidator(
    otherCounterPartyName: string,
    control: AbstractControl
  ): KeyValue | null {
    // validator makes sure that counterpartyB is not the same as counter Party B
    // And makes sure that one of the two are in the users group
    const otherCounterPartyControl =
      control.parent.controls[otherCounterPartyName];
    if (otherCounterPartyControl.value === control.value) {
      otherCounterPartyControl.setErrors({
        invalidEntry: { value: control.value }
      });
      this.reasonCantSave = 'SAME_COUNTERPARTIES';
      return { invalidEntry: { value: control.value } };
    }

    // if none are in the same group as the user
    if (
      !(
        this.sameGroupIdentifiers.includes(control.value) ||
        this.sameGroupIdentifiers.includes(otherCounterPartyControl.value)
      ) &&
      otherCounterPartyControl.value
    ) {
      this.reasonCantSave = 'MISSING_INTERNAL_ENTITY';
      return { invalidEntry: { value: control.value } };
    }

    if (otherCounterPartyControl.value) {
      otherCounterPartyControl.setErrors(null);
    }
    this.reasonCantSave = null;
    return null;
  }

  modalChanged(val: boolean) {
    this.tradesCaptureService.setShowCsa = val;
  }

  private updateLocalEntities(entities: Entity[]) {
    this.identifiers = this.legService.updateEntities(entities);
  }
}
